import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { ReassignListsDialog } from 'components/common/pitch-lists/reassign-lists';
import { CommonTableHoC } from 'components/common/table';
import { TeamsTableToolbar } from 'components/sections/admin-portal/teams/toolbar';
import { TeamsContext } from 'contexts/admin/teams.context';
import lightFormat from 'date-fns/lightFormat';
import parseISO from 'date-fns/parseISO';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { MlbAbbreviation } from 'lib_ts/enums/batrack.enums';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { TeamDialog } from 'lib_ts/enums/teams.enums';
import { IAdminTeam } from 'lib_ts/interfaces/i-team';
import { IPitchListPutManyRequest } from 'lib_ts/interfaces/pitches/i-pitch-list';
import { isNil } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMiscStore from 'stores/use-misc-store';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

export const TeamsTable = () => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);

  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const { teams, openDialog } = useContext(TeamsContext);

  const columns = useMemo<ITypedTableColumn<IAdminTeam>[]>(
    () => [
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        actions: [
          {
            label: 'common.edit',
            onClick: (m) =>
              openDialog({
                mode: TeamDialog.Editor,
                team: m,
              }),
          },
          {
            label: 'admin.restore-lists',
            invisibleFn: () => !userRoleFeatureFlags.adminTeams.restoreLists,
            suffixIcon: <SuperAdminIcon />,
            color: RADIX.COLOR.WARNING,
            onClick: (m) =>
              openDialog({
                mode: TeamDialog.Restore,
                team: m,
              }),
          },
          {
            label: 'common.reassign-lists',
            onClick: (m) => {
              const payload: IPitchListPutManyRequest = {
                filter: {
                  _parent_id: m._id,
                  _parent_def: PitchListOwner.Team,
                },
                update: {
                  processed: new Date(),
                  process_notes: `reassigned from team ${m.name} by admin ${current.email}`,
                },
              };

              setReassignListsPayload(payload);
              setDialogReassign(Date.now());
            },
            color: RADIX.COLOR.WARNING,
          },
        ],
      },
      {
        label: 'common.created',
        key: '_created',
        formatFn: (m) => lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT),
      },
      {
        label: 'common.name',
        key: 'name',
        formatFn: (m) => {
          if (m.abbreviation && m.abbreviation !== MlbAbbreviation.None) {
            return `${m.name} (${m.abbreviation})`;
          }

          return m.name;
        },
      },
      {
        label: 'common.game-status',
        key: 'isInGame',
        formatFn: (m) => {
          if (isNil(m.isInGame)) {
            return null;
          }

          const color: RadixColor | undefined = (() => {
            switch (m.isInGame) {
              case true: {
                return RADIX.COLOR.WARNING;
              }

              case false:
              default: {
                return RADIX.COLOR.SUCCESS;
              }
            }
          })();

          return (
            <Text color={color}>{m.isInGame ? 'In Progress' : 'No Game'}</Text>
          );
        },
      },
      {
        label: 'admin.domains',
        key: 'email_domains',
        sortRowsFn: (a, b, dir: number) =>
          ((a.email_domains ?? []).length > (b.email_domains ?? []).length
            ? 1
            : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" gap="1">
            {m.email_domains?.map((d) => (
              <Box key={`domain-${d}`}>
                <Badge>{d}</Badge>
              </Box>
            )) ?? null}
          </Flex>
        ),
      },
      {
        label: 'admin.machines',
        key: 'count_machines',
        align: 'right',
      },
      {
        label: 'admin.users',
        key: 'count_users',
        align: 'right',
      },
      {
        label: 'admin.videos',
        key: 'count_videos',
        align: 'right',
      },
    ],
    [current.email, userRoleFeatureFlags]
  );

  const [reassignListsPayload, setReassignListsPayload] =
    useState<IPitchListPutManyRequest>({ filter: {}, update: {} });

  const [dialogReassign, setDialogReassign] = useState<number>();

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminTeamList,
    total: teams.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'name',
      dir: -1,
    },
  };

  const select: ITableSelectable = {
    enableSelect: true,
  };

  return (
    <ErrorBoundary identifier="TeamsTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <CommonTableHoC
            dataTestID="AdminTeams"
            identifier="AdminTeamsTable"
            toolbarContent={<TeamsTableToolbar />}
            displayColumns={columns}
            displayData={teams}
            checkboxColumnIndex={0}
            enableListener={!anyOpenDialogs}
            {...pagination}
            {...select}
            {...sort}
            vFlex
          />
        }
      />

      {dialogReassign && (
        <ReassignListsDialog
          key={dialogReassign}
          identifier="TeamsTableReassignListsDialog"
          refPayload={reassignListsPayload}
          onClose={() => setDialogReassign(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
